import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { IPhoneNumber, IPhoneNumberGroup } from '../../interface'
import { PhoneNumbers } from '../PhoneNumbers'
import { PhoneNumberSearchInput } from '../PhoneNumberSearchInput'
import { AssignToCampaignModal } from '../modals/AssignToCampaignModal'
import { filterPhoneNumberGroups } from '../../utils'
import { usePhoneNumbers } from '../../hooks'

interface InactiveNumbersViewProps {
   groupedInactivePhoneNumbers: IPhoneNumberGroup[]
   listMaxHeight?: string
}

export const InactiveNumbersView = ({
   groupedInactivePhoneNumbers,
   listMaxHeight = 'calc(100vh - 200px)',
}: InactiveNumbersViewProps) => {
   const { assignPhoneNumber, allCampaigns } = usePhoneNumbers()
   const [searchQuery, setSearchQuery] = useState('')
   const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
   const [numberToAssign, setNumberToAssign] = useState<IPhoneNumber | null>(
      null
   )
   const [selectedNumbers, setSelectedNumbers] = useState<Set<string>>(
      new Set()
   )
   const [isBulkAssign, setIsBulkAssign] = useState(false)
   const [isAssigning, setIsAssigning] = useState(false)

   const filteredGroups = useMemo(
      () => filterPhoneNumberGroups(groupedInactivePhoneNumbers, searchQuery),
      [groupedInactivePhoneNumbers, searchQuery]
   )

   const handleAssignClick = (number: IPhoneNumber) => {
      setNumberToAssign(number)
      setIsBulkAssign(false)
      setIsAssignModalOpen(true)
   }

   const handleBulkAssignClick = () => {
      setIsBulkAssign(true)
      setIsAssignModalOpen(true)
   }

   const handleConfirmAssign = async (campaignId: string) => {
      setIsAssigning(true)
      try {
         if (isBulkAssign) {
            // Bulk assignment
            await assignPhoneNumber(Array.from(selectedNumbers), campaignId)
            setSelectedNumbers(new Set())
         } else if (numberToAssign) {
            // Single assignment
            await assignPhoneNumber(
               [numberToAssign.phoneNumber.phoneNumber],
               campaignId
            )
         }
         setIsAssignModalOpen(false)
         setNumberToAssign(null)
         setIsBulkAssign(false)
      } catch (error) {
         console.error('Error assigning number to campaign:', error)
      } finally {
         setIsAssigning(false)
      }
   }

   const handleCloseAssignModal = () => {
      if (isAssigning) return
      setIsAssignModalOpen(false)
      setNumberToAssign(null)
      setIsBulkAssign(false)
   }

   const handleCheckboxChange = (number: IPhoneNumber) => {
      setSelectedNumbers((prev) => {
         const next = new Set(prev)
         const id = number.phoneNumber.phoneNumber
         if (next.has(id)) {
            next.delete(id)
         } else {
            next.add(id)
         }
         return next
      })
   }

   const anyItemChecked = selectedNumbers.size > 0

   return (
      <div className="flex flex-col h-full">
         {/* Fixed Header Section */}
         <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex-shrink-0"
         >
            <PhoneNumberSearchInput
               value={searchQuery}
               onChange={setSearchQuery}
               placeholder="Search Phone numbers"
            />
         </motion.div>

         {/* Bulk Actions Bar */}
         {anyItemChecked && (
            <motion.div
               initial={{ opacity: 0, y: -10 }}
               animate={{ opacity: 1, y: 0 }}
               exit={{ opacity: 0, y: -10 }}
               className="flex items-center justify-between bg-[#9061DA]/20 rounded-lg p-4 mt-4"
            >
               <p className="text-white text-sm font-medium">
                  {selectedNumbers.size} number
                  {selectedNumbers.size !== 1 ? 's' : ''} selected
               </p>
               <div className="flex items-center gap-x-2">
                  <button
                     onClick={() => setSelectedNumbers(new Set())}
                     className="px-4 py-2 text-white/70 hover:text-white text-sm font-medium transition-colors"
                  >
                     Clear Selection
                  </button>
                  <button
                     onClick={handleBulkAssignClick}
                     className="px-4 py-2 bg-[#9061DA] hover:bg-[#9061DA]/80 text-white text-sm font-medium rounded transition-colors"
                  >
                     Assign to Campaign
                  </button>
               </div>
            </motion.div>
         )}

         {/* Scrollable List Section */}
         <div
            className="overflow-y-auto mt-6"
            style={{ maxHeight: listMaxHeight }}
         >
            {filteredGroups.length > 0 ? (
               <PhoneNumbers
                  numberGroups={filteredGroups}
                  showAssignButton={true}
                  onAssign={handleAssignClick}
                  checkboxMode={true}
                  selectedNumbers={selectedNumbers}
                  onCheckboxChange={handleCheckboxChange}
                  anyItemChecked={anyItemChecked}
               />
            ) : (
               <div className="flex items-center justify-center py-12">
                  <p className="text-white/40 text-sm">
                     No phone numbers found
                     {searchQuery && ` for "${searchQuery}"`}
                  </p>
               </div>
            )}
         </div>

         <AssignToCampaignModal
            isOpen={isAssignModalOpen}
            onClose={handleCloseAssignModal}
            onConfirm={handleConfirmAssign}
            phoneNumber={numberToAssign || undefined}
            campaigns={allCampaigns}
            isBulkMode={isBulkAssign}
            bulkCount={selectedNumbers.size}
            isLoading={isAssigning}
         />
      </div>
   )
}
